import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { IntlProvider, useIntl, FormattedMessage } from "react-intl";

import { Button } from "@learningblocks/shared-components/lib/Button";
import { LogEvent } from "@learningblocks/shared-components/lib/EventLogger";

import { fetchTranslations } from "./locales/config";
import style from "./style.module.scss";

/**
 * PopoutFooter Component
 *
 * This component serves as a footer for the NoteBookComponent, providing
 * navigation buttons (previous and next) and event logging functionality.
 */
export function PopoutFooter(props) {
  // State for storing fetched translations
  const [translations, setTranslations] = useState({
    locale: "en",
    messages: {},
  });

  // Effect hook for fetching translations when the language changes
  useEffect(() => {
    fetchTranslations(props.lang).then((data) => {
      setTranslations(data);
    });
  }, [props.lang]);

  /**
   * MainComponent - The main render function for the PopoutFooter.
   * It displays the Previous and Next buttons with appropriate logging events.
   */
  const MainComponent = () => {
    const intl = useIntl();
    return (
      <div
        className={style.popoutFooter}
        style={{
          justifyContent:
            props.sectionIndex === 0 ? "flex-end" : "space-between",
        }}
      >
        {/* Logging Event and Previous Button */}
        <LogEvent
          logEventProps={{
            event_type: `sim_${props.section}_prevsection_btn clicked`,
            event_schema: "track",
          }}
          actionProps={{ onClick: { action: "click" } }}
          elementType="button"
        >
          <Fragment>
            {props.sectionIndex > 0 && (
              <Button
                onClick={props.prevSection}
                ariaLabel={intl.formatMessage({
                  id: "PREV_BUTTON_LABEL",
                  defaultMessage:
                    "Previous button. Select to go back to the previous section.",
                    description: "Aria label for the previous section button"
                })}
                className={style.popoutButton}
                type="secondary"
              >
                <FormattedMessage
                  id="PREV_BUTTON_TITLE"
                  defaultMessage="< Previous section"
                  description="Shown on left side of screeen for LTR and RTL languages"
                />
              </Button>
            )}
          </Fragment>
        </LogEvent>
        {/* Logging Event and Next Button */}
        <LogEvent
          logEventProps={{
            event_type: `sim_${props.section}_nextsection_btn clicked`,
            event_schema: "track",
          }}
          actionProps={{ onClick: { action: "click" } }}
          elementType="button"
        >
          <Button
            onClick={props.nextSection}
            ariaLabel={
              props.sectionIndex === props.sectionLength - 1
                ? intl.formatMessage({
                    id: "ENDS_BUTTON_LABEL",
                    defaultMessage:
                      "End simulation button. Select to end the simulation.",
                      description: ""
                  })
                : intl.formatMessage({
                    id: "NEXT_BUTTON_LABEL",
                    defaultMessage:
                      "Next button. Select to advance to the next section.",
                      description: ""
                  })
            }
            className={style.popoutButton}
            type="primary"
          >
            {props.sectionIndex === props.sectionLength - 1 ? (
              <FormattedMessage
                id="ENDS_BUTTON_TITLE"
                defaultMessage="End simulation"
                description=""
              />
            ) : (
              <FormattedMessage
                id="NEXT_BUTTON_TITLE"
                defaultMessage="Next section >"
                description="Shown on right side of screeen for LTR and RTL languages"
              />
            )}
          </Button>
        </LogEvent>
      </div>
    );
  };

  return (
    <IntlProvider {...translations}>
      <MainComponent {...props} />
    </IntlProvider>
  );
}

// PropTypes for the PopoutFooter Component
PopoutFooter.propTypes = {
  className: PropTypes.string, // CSS classname for styling
  lang: PropTypes.oneOf([
    // Supported languages
    "en",
    "az",
    "ar",
    "ja",
    "nl",
    "de",
    "es",
    "fr",
    "it",
    "pl",
    "ko",
    "pt",
    "tr",
    "zh",
    "zu",
  ]),
  sectionIndex: PropTypes.number, // Current section index in the notebook
  sectionLength: PropTypes.number, // Total number of sections in the notebook
  section: PropTypes.string, // Section title for logging purposes
  nextSection: PropTypes.func, // Function to be called when the "Next" button is clicked
  prevSection: PropTypes.func, // Function to be called when the "Previous" button is clicked
};

// Default properties for the PopoutFooter Component
PopoutFooter.defaultProps = {
  className: "popoutFooter",
  lang: "en",
};
